import {
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    Animated
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    getSlideStyle,
    usePresence,
    Marker,
    Switch,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    FileSpreadsheet as FileSpreadsheetIcon,
    TriangleAlert as TriangleAlertIcon,
    CircleCheck as CircleCheckIcon,
    CircleAlert as CircleAlertIcon,
    FileText as FileTextIcon,
    UserPlus as UserPlusIcon,
    Loader as LoaderIcon,
    Check as CheckIcon,
    Info as InfoIcon
} from "lucide-react-native";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Marker";

const USAGE_CODE = `const [areStepsOpen, setAreStepsOpen] = useState(false);

<Marker
    icon={LoaderIcon}
    isPending={true}
>
    Scanning report…
</Marker>

<Marker
    onPress={() => setAreStepsOpen(!areStepsOpen)}
    isExpanded={areStepsOpen}
    icon={CheckIcon}
>
    4 files reviewed
</Marker>

<Marker
    variant="separator"
    isUnread={true}
>
    Today
</Marker>`;

const TYPES = [
    {
        textKey: "markerPage.typeNeutral",
        icon: CheckIcon,
        type: "neutral"
    },
    {
        textKey: "markerPage.typeSuccess",
        icon: CircleCheckIcon,
        type: "success"
    },
    {
        textKey: "markerPage.typeWarning",
        icon: TriangleAlertIcon,
        type: "warning"
    },
    {
        textKey: "markerPage.typeDanger",
        icon: CircleAlertIcon,
        type: "danger"
    },
    {
        textKey: "markerPage.typeInfo",
        icon: InfoIcon,
        type: "info"
    }
] as const;

const SIZES = [
    "medium",
    "small"
] as const;

const STEPS_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    gap: 8
};

const ROW_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    gap: 8
};

type StepsProps = {
    children: ReactNode;
    isOpen: boolean;
};

const Steps = ({
    children,
    isOpen
}: StepsProps) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        isReduceMotionEnabled,
        isRendered,
        progress
    } = usePresence({
        enterIntent: "listInsert",
        exitIntent: "listRemove",
        isVisible: isOpen
    });

    if(!isRendered) {
        return null;
    }

    return <Animated.View
        style={getSlideStyle({
            offset: spaces.spacingSm,
            isReduceMotionEnabled,
            progress
        })}
    >
        <div
            style={STEPS_STYLE}
        >
            {children}
        </div>
    </Animated.View>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("markerPage.typesTitle"),
        id: "types"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("markerPage.separatorTitle"),
        id: "separator"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const MarkerPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        areStepsOpen,
        setAreStepsOpen
    ] = useState(false);

    const [
        isPending,
        setIsPending
    ] = useState(false);

    const [
        isStuck,
        setIsStuck
    ] = useState(false);

    const handleToggleSteps = () => setAreStepsOpen(!areStepsOpen);

    const handleTogglePending = () => setIsPending(!isPending);

    const handleToggleStuck = () => setIsStuck(!isStuck);

    return <Article>
        <ComponentHeader
            lead={t("markerPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("markerPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("markerPage.overviewCaption")}
                isStacked={true}
            >
                <Marker
                    variant="separator"
                >
                    {t("markerPage.today")}
                </Marker>
                <Marker
                    onPress={handleToggleSteps}
                    isExpanded={areStepsOpen}
                    icon={CheckIcon}
                >
                    {t("markerPage.reviewed")}
                </Marker>
                <Steps
                    isOpen={areStepsOpen}
                >
                    <Marker
                        icon={FileTextIcon}
                        size="small"
                    >
                        {t("sample.fileName")}
                    </Marker>
                    <Marker
                        icon={FileSpreadsheetIcon}
                        size="small"
                    >
                        {t("markerPage.fileBudget")}
                    </Marker>
                </Steps>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("markerPage.variantsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Marker
                    icon={UserPlusIcon}
                >
                    {t("markerPage.variantInline")}
                </Marker>
                <Marker
                    icon={FileTextIcon}
                    variant="border"
                >
                    {t("markerPage.variantBorder")}
                </Marker>
                <Marker
                    variant="separator"
                >
                    {t("markerPage.variantSeparator")}
                </Marker>
            </Preview>
        </Section>
        <Section
            title={t("markerPage.typesTitle")}
            id="types"
        >
            <Paragraph>
                {t("markerPage.typesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    TYPES.map((item) => <Marker
                        icon={item.icon}
                        type={item.type}
                        key={item.type}
                    >
                        {t(item.textKey)}
                    </Marker>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("markerPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("markerPage.statesCaption")}
                isStacked={true}
            >
                <Marker
                    isPending={isPending}
                    icon={LoaderIcon}
                >
                    {t("markerPage.scanning")}
                </Marker>
                <Switch
                    title={t("markerPage.pendingSwitch")}
                    onPress={handleTogglePending}
                    isChecked={isPending}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("markerPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => <div
                        style={ROW_STYLE}
                        key={size}
                    >
                        <Text
                            variant="bodySmallSize"
                            color="low"
                        >
                            {`size="${size}"`}
                        </Text>
                        <Marker
                            icon={FileTextIcon}
                            size={size}
                        >
                            {t("markerPage.variantBorder")}
                        </Marker>
                    </div>)
                }
                <div
                    style={ROW_STYLE}
                >
                    <Text
                        variant="bodySmallSize"
                        color="low"
                    >
                        {"size=\"small\" isCentered"}
                    </Text>
                    <Marker
                        icon={UserPlusIcon}
                        isCentered={true}
                        size="small"
                    >
                        {t("markerPage.variantInline")}
                    </Marker>
                </div>
            </Preview>
        </Section>
        <Section
            title={t("markerPage.separatorTitle")}
            id="separator"
        >
            <Paragraph>
                {t("markerPage.separatorText")}
            </Paragraph>
            <Preview
                caption={t("markerPage.separatorCaption")}
                isStacked={true}
            >
                <Marker
                    variant="separator"
                    isStuck={isStuck}
                >
                    {t("markerPage.today")}
                </Marker>
                <Marker
                    variant="separator"
                    isUnread={true}
                >
                    {t("markerPage.unread")}
                </Marker>
                <Switch
                    title={t("markerPage.stuckSwitch")}
                    onPress={handleToggleStuck}
                    isChecked={isStuck}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("markerPage.a11yPress"),
                    t("markerPage.a11yPending"),
                    t("markerPage.a11yHidden"),
                    t("markerPage.a11ySeparator"),
                    t("markerPage.a11yParts")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("markerPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        description: t("markerPage.propIcon"),
                        type: "LucideIcon",
                        name: "icon"
                    },
                    {
                        type: "\"inline\" | \"border\" | \"separator\"",
                        description: t("markerPage.propVariant"),
                        defaultValue: "\"inline\"",
                        name: "variant"
                    },
                    {
                        type: "\"neutral\" | \"success\" | \"warning\" | \"danger\" | \"info\"",
                        description: t("markerPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("markerPage.propSize"),
                        type: "\"small\" | \"medium\"",
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("markerPage.propIsPending"),
                        defaultValue: "false",
                        name: "isPending",
                        type: "boolean"
                    },
                    {
                        description: t("markerPage.propIsUnread"),
                        defaultValue: "false",
                        name: "isUnread",
                        type: "boolean"
                    },
                    {
                        description: t("markerPage.propIsCentered"),
                        defaultValue: "false",
                        name: "isCentered",
                        type: "boolean"
                    },
                    {
                        description: t("markerPage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("markerPage.propIsExpanded"),
                        name: "isExpanded",
                        type: "boolean"
                    },
                    {
                        description: t("markerPage.propIsShowChevron"),
                        name: "isShowChevron",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("markerPage.propIsSticky"),
                        defaultValue: "false",
                        name: "isSticky",
                        type: "boolean"
                    },
                    {
                        description: t("markerPage.propIsStuck"),
                        name: "isStuck",
                        type: "boolean"
                    },
                    {
                        description: t("markerPage.propOnStuckChange"),
                        type: "(isStuck: boolean) => void",
                        name: "onStuckChange"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("markerPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("markerPage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("markerPage.partsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("markerPage.propPartIcon"),
                        type: "LucideIcon",
                        name: "icon"
                    },
                    {
                        description: t("markerPage.propPartContent"),
                        type: "ReactNode",
                        name: "children"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default MarkerPage;
